import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { QrCode, Download, CheckCircle, XCircle, RefreshCw, Scan, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QRCodePage = () => {
  const { user } = useOutletContext();
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationToken, setValidationToken] = useState('');
  const [validationResult, setValidationResult] = useState(null);

  const handleGenerateQR = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/qr/generate`,
        {},
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const imageUrl = URL.createObjectURL(response.data);
      setQrImageUrl(imageUrl);
      toast.success('QR Code gerado com sucesso!');
    } catch (error) {
      console.error('Error generating QR:', error);
      toast.error('Erro ao gerar QR Code');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadQR = () => {
    if (!qrImageUrl) return;
    
    const link = document.createElement('a');
    link.href = qrImageUrl;
    link.download = `qr_consentimento_${user.user_id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR Code descarregado!');
  };

  const handleValidateQR = async () => {
    if (!validationToken.trim()) {
      toast.error('Por favor introduza o token do QR Code');
      return;
    }
    
    setValidating(true);
    setValidationResult(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/qr/validate`,
        null,
        { 
          params: { token: validationToken },
          withCredentials: true 
        }
      );
      
      setValidationResult({
        success: true,
        message: 'QR Code válido!',
        userId: response.data.user_id
      });
      toast.success('QR Code validado com sucesso!');
    } catch (error) {
      console.error('Error validating QR:', error);
      setValidationResult({
        success: false,
        message: error.response?.data?.detail || 'QR Code inválido ou expirado'
      });
      toast.error('QR Code inválido ou expirado');
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="qr-code-page">
      <div>
        <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          QR Code para Consentimentos
        </h1>
        <p className="text-slate-600">
          Gere e valide QR Codes para confirmação de consentimentos presenciais
        </p>
      </div>

      <Tabs defaultValue="generate" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="generate" className="flex items-center gap-2">
            <QrCode className="w-4 h-4" />
            Gerar QR Code
          </TabsTrigger>
          <TabsTrigger value="validate" className="flex items-center gap-2">
            <Scan className="w-4 h-4" />
            Validar QR Code
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-[#10B981]" />
                  Gerar Novo QR Code
                </CardTitle>
                <CardDescription>
                  Gere um QR Code único para validação do seu consentimento. O código é válido por 15 minutos.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Como funciona:</h4>
                  <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
                    <li>Clique em "Gerar QR Code" para criar um código único</li>
                    <li>Apresente o QR Code ao operador autorizado</li>
                    <li>O operador irá digitalizar e validar o código</li>
                    <li>O consentimento será confirmado presencialmente</li>
                  </ol>
                </div>

                <Button
                  data-testid="generate-qr-button"
                  onClick={handleGenerateQR}
                  disabled={generating}
                  className="w-full bg-[#10B981] hover:bg-[#10B981]/90 h-12"
                >
                  {generating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      A gerar...
                    </>
                  ) : (
                    <>
                      <QrCode className="w-4 h-4 mr-2" />
                      Gerar QR Code
                    </>
                  )}
                </Button>

                <div className="text-xs text-slate-500 text-center">
                  <ShieldCheck className="w-4 h-4 inline mr-1" />
                  O QR Code expira automaticamente após 15 minutos por segurança
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>QR Code Gerado</CardTitle>
                <CardDescription>
                  Apresente este código ao operador para validação
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center min-h-[300px]">
                {qrImageUrl ? (
                  <div className="space-y-4 text-center">
                    <div className="p-4 bg-white rounded-lg border-2 border-slate-200 inline-block">
                      <img 
                        src={qrImageUrl} 
                        alt="QR Code para consentimento" 
                        className="w-64 h-64"
                        data-testid="qr-code-image"
                      />
                    </div>
                    
                    <div className="flex items-center justify-center gap-2">
                      <Badge className="bg-amber-50 text-amber-700 border-amber-200">
                        Válido por 15 minutos
                      </Badge>
                    </div>
                    
                    <div className="flex gap-3">
                      <Button
                        data-testid="download-qr-button"
                        variant="outline"
                        onClick={handleDownloadQR}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Descarregar
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleGenerateQR}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Gerar Novo
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-slate-500">
                    <QrCode className="w-24 h-24 mx-auto mb-4 text-slate-300" />
                    <p>Clique em "Gerar QR Code" para criar o seu código</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="validate">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scan className="w-5 h-5 text-[#3B82F6]" />
                  Validar QR Code
                </CardTitle>
                <CardDescription>
                  Introduza o token do QR Code para validar o consentimento
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Para Operadores:</h4>
                  <p className="text-sm text-slate-600">
                    Digitalize o QR Code do titular usando uma aplicação de leitura de QR. 
                    O token extraído deve ser introduzido abaixo para validar o consentimento.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="qr-token">Token do QR Code</Label>
                  <Input
                    id="qr-token"
                    data-testid="qr-token-input"
                    value={validationToken}
                    onChange={(e) => setValidationToken(e.target.value)}
                    placeholder="Cole o token do QR Code aqui..."
                    className="font-mono"
                  />
                </div>

                <Button
                  data-testid="validate-qr-button"
                  onClick={handleValidateQR}
                  disabled={validating || !validationToken.trim()}
                  className="w-full bg-[#3B82F6] hover:bg-[#3B82F6]/90 h-12"
                >
                  {validating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      A validar...
                    </>
                  ) : (
                    <>
                      <Scan className="w-4 h-4 mr-2" />
                      Validar QR Code
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Resultado da Validação</CardTitle>
                <CardDescription>
                  Estado da validação do QR Code
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center min-h-[300px]">
                {validationResult ? (
                  <div className="text-center space-y-4">
                    {validationResult.success ? (
                      <>
                        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                          <CheckCircle className="w-10 h-10 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-emerald-600">{validationResult.message}</h3>
                          {validationResult.userId && (
                            <p className="text-sm text-slate-600 mt-2">
                              ID do Utilizador: <code className="bg-slate-100 px-2 py-1 rounded">{validationResult.userId}</code>
                            </p>
                          )}
                        </div>
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          Consentimento Confirmado
                        </Badge>
                      </>
                    ) : (
                      <>
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                          <XCircle className="w-10 h-10 text-red-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-red-600">{validationResult.message}</h3>
                          <p className="text-sm text-slate-600 mt-2">
                            O QR Code pode estar expirado ou já ter sido utilizado.
                          </p>
                        </div>
                        <Badge className="bg-red-50 text-red-700 border-red-200">
                          Validação Falhou
                        </Badge>
                      </>
                    )}
                    
                    <Button
                      variant="outline"
                      onClick={() => {
                        setValidationResult(null);
                        setValidationToken('');
                      }}
                    >
                      Nova Validação
                    </Button>
                  </div>
                ) : (
                  <div className="text-center text-slate-500">
                    <Scan className="w-24 h-24 mx-auto mb-4 text-slate-300" />
                    <p>Introduza um token e clique em "Validar" para verificar</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Information Card */}
      <Card className="border-slate-200 bg-slate-50">
        <CardHeader>
          <CardTitle className="text-lg">Informação sobre QR Codes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-2">Segurança</h4>
              <p className="text-slate-600">
                Cada QR Code é único e criptograficamente seguro. Expira automaticamente após 15 minutos para proteger contra uso indevido.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-2">Conformidade RGPD</h4>
              <p className="text-slate-600">
                A validação por QR Code serve como prova adicional de consentimento conforme o Artigo 7º do RGPD.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-2">Uso Único</h4>
              <p className="text-slate-600">
                Cada código só pode ser usado uma vez. Após validação, o código é automaticamente invalidado.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QRCodePage;
